// ABOUTME: Dynamic modal component that renders input fields based on field configuration array
// ABOUTME: Supports text, integer, date, list, and boolean input types with validation and submission
import { Box, useTheme, InputBase, ToggleButton, ToggleButtonGroup } from "@mui/material"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import SHModal from "../../theme/Modal/SHModal"
import Shbutton from "../../theme/buttons/Shbutton"
import ButtonGroup from "../../theme/buttons/ButtonGroup"
import { tokens } from "../../../theme"
import Subheading from "../../theme/text/Subheading"
import Text from "../../theme/text/Text"
import NumberInput from "../../theme/inputs/NumberInput"
import ShSwitch from "../../theme/inputs/ShSwitch"
import ShDateRangePicker from "../../theme/ShDateRangePicker"
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { InputDropdown } from "../../theme/dropdowns/InputDropdown"
import EMGeneralExplainerBox from "../ModalsPopover/explainers/EMGeneralExplainerBox"
import SettingsSaveButton from "../Settings/SettingsSaveButton"
import cx from "classnames"

/**
 * MDynamicValues Modal Component
 *
 * Props:
 * - open: boolean - Controls modal visibility
 * - handleClose: function - Function to call when modal closes
 * - title: string - Modal title
 * - subtitle: string (optional) - Modal subtitle
 * - fields: array - Array of field configuration objects
 *   Field object structure:
 *   {
 *     name: string - Field identifier/key
 *     label: string - Field label (translation key)
 *     type: "text" | "integer" | "date" | "list" | "boolean" | "switch" | "custom" - Input type
 *     defaultValue: any (optional) - Default value for the field
 *     required: boolean (optional) - Whether field is required
 *     options: array (for list type) - Array of options
 *     optionsFilter: function (optional) - Function to filter options based on form values (option, formValues) => boolean
 *     hidden: function (optional) - Hides the field on the value of another field (formValues) => boolean.
 *            A hidden field keeps its value and is never required
 *     multiple: boolean (optional) - Whether list allows multiple selections
 *     search: boolean (optional) - Whether list has search functionality
 *     alignTop: boolean (optional) - Lines the label up with the first row of a field that
 *            grows over several rows, instead of centering it beside the whole field
 *     render: function (for custom type) - Renders a caller-provided input.
 *            Receives ({ value, onChange, formValues, field }) and returns a React node.
 *     onChange: function (optional) - Called with the new value when the field changes, for a
 *            screen that rebuilds the other fields on a pick (value, formValues) => void
 *   }
 * - message: node (optional) - Custom content rendered above the fields (e.g. an info box)
 * - onSave: function (optional) - Renders a save button beside confirm, enabled once the values
 *   differ from the defaults, that hands the current values to onSave without closing
 * - maxWidth: number (optional) - Width of the modal, for fields that need room (default 500)
 * - onConfirm: function - Function to call with form data on confirm
 */
const MDynamicValues = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    // Initialize form values from field configuration
    const initializeValues = () => {
        const values = {};
        props.fields?.forEach(field => {
            values[field.name] = field.defaultValue !== undefined ? field.defaultValue :
                                 field.type === "integer" ? 0 :
                                 field.type === "date" || field.type === "custom" ? null :
                                 field.type === "boolean" || field.type === "switch" ? false :
                                 field.type === "list" && field.multiple ? [] : "";
        });
        return values;
    };

    const [formValues, setFormValues] = useState(initializeValues());

    // Reinitialize form values when modal opens or fields change
    useEffect(() => {
        if (props.open) {
            setFormValues(initializeValues());
        }
    }, [props.open, props.fields]);

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            confirm();
        }
    };

    const updateFieldValue = (fieldName, value, field) => {
        const updates = {[fieldName]: value}
        if (field?.deductFrom) {
            const prop = props.fields.find(x => x.name === field.deductFrom);
            updates[field.deductFrom] = prop.defaultValue - value;
        }
        setFormValues(prev => ({
            ...prev,
            ...updates
        }));
        field?.onChange?.(value, formValues);
    };

    const hasChanges = JSON.stringify(formValues) !== JSON.stringify(initializeValues());
    const [isSaving, setIsSaving] = useState(false);

    const save = async () => {
        if (!props.onSave || isSaving) return;
        setIsSaving(true);
        try {
            await props.onSave(formValues);
        } finally {
            setIsSaving(false);
        }
    };

    const confirm = () => {
        // Validate required fields
        const hasEmptyRequired = props.fields?.some(field =>
            field.required && !field.hidden?.(formValues) && (!formValues[field.name] || formValues[field.name] === "")
        );

        if (hasEmptyRequired) {
            // Could add error handling here
            return;
        }

        props.onConfirm(formValues);
        props.handleClose();
    };

    const renderField = (field) => {
        const value = formValues[field.name];

        switch (field.type) {
            case "text":
                return (
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            borderRadius: 4,
                            display: "flex",
                            height: 32
                        }}
                    >
                        <InputBase
                            sx={{
                                px: 2,
                                flex: 1,
                                color: colors.txt["primary"],
                                lineHeight: "32px",
                            }}
                            value={value}
                            onChange={(e) => updateFieldValue(field.name, e.target.value, field)}
                            placeholder={field.placeholder ? t(field.placeholder) : ""}
                        />
                    </Box>
                );

            case "integer":
                return (
                    <Box
                        sx={{
                            flex: 1,
                            backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            borderRadius: 4,
                            display: "flex",
                            height: 32,
                            opacity: field.disabled ? 0.6 : 1,
                            pointerEvents: field.disabled ? "none" : "auto"
                        }}
                    >
                        <NumberInput
                            btnSize={"24"}
                            value={value}
                            change={(newValue) => updateFieldValue(field.name, newValue, field)}
                            disabled={field.disabled}
                            min={field.minValue ?? null}
                        />
                    </Box>
                );

            case "date":
                return (
                    <ShDateRangePicker
                        explicit
                        singleSelect={true}
                        startDate={value}
                        shouldHide={!value}
                        select={(data) => {
                            updateFieldValue(field.name, data[0].formattedStartDate,field);
                        }}
                        size={32}
                        sx={{
                            backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            borderRadius: 4,
                            "&:hover": {
                                background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200],
                            }
                        }}
                    />
                );

            case "list":
                // Apply options filter if provided
                const filteredOptions = field.optionsFilter
                    ? (field.options ?? []).filter(option => field.optionsFilter(option, formValues))
                    : (field.options ?? []);

                const getSelected = () => {
                    if (!filteredOptions.length) return field.multiple ? [] : -1;

                    if (field.multiple) {
                        // For multiple selection, return array of indices
                        if (!Array.isArray(value)) return [];
                        return value.map(id => filteredOptions.findIndex(item => item.id === id)).filter(idx => idx !== -1);
                    } else {
                        // For single selection, return single index
                        return filteredOptions.findIndex(item => item.id === value) ?? -1;
                    }
                };

                const handleChange = (selected) => {
                    if (field.multiple) {
                        // For multiple selection, selected is an array of indices
                        const selectedIds = Array.isArray(selected)
                            ? selected.map(idx => filteredOptions[idx]?.id).filter(Boolean)
                            : [];
                        updateFieldValue(field.name, selectedIds);
                    } else {
                        // For single selection, selected is a single index
                        updateFieldValue(field.name, filteredOptions[selected]?.id, field);
                    }
                };

                return (
                    <InputDropdown
                        name="name"
                        displayName="name"
                        selected={getSelected()}
                        options={filteredOptions}
                        noSelection={t("choose_option")}
                        onChange={handleChange}
                        closeOnSelection={!field.multiple}
                        multiple={field.multiple ?? false}
                        {...(field.search && {
                            searchOptions: {
                                enabled: true,
                                placeholder: t("search")
                            },
                            styles: {
                                minWidth: 300
                            },
                            disallowDeselection: true
                        })}
                    />
                );

            case "boolean":
                return (
                    <ToggleButtonGroup
                        color={value === true ? "green" : "red"}
                        value={value === true ? "true" : "false"}
                        exclusive
                        onChange={(e) => updateFieldValue(field.name, e.target.value === "true")}
                        aria-label={field.name}
                        className={cx(`${value}`, {
                            'active': value === true
                        })}
                        sx={{
                            background: colors.red[100],
                            padding: 0.5,
                            borderRadius: 5,
                            transition: "250ms",
                            "&.active": {
                                background: colors.green[100],
                                transition: "250ms",
                            },
                        }}
                    >
                        <ToggleButton
                            className={cx('', {
                                'active': value !== true
                            })}
                            sx={{
                                padding: 0,
                                paddingX: 2,
                                border: 0,
                                lineHeight: "32px",
                                textTransform: "unset",
                                borderRadius: "32px !important",
                                color: colors.green[400],
                                transition: "250ms",
                                "&.active, &.Mui-selected, &.Mui-selected:hover": {
                                    background: colors.red[400],
                                    color: theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"
                                }
                            }}
                            value="false"
                        >
                            {t('no')}
                        </ToggleButton>
                        <ToggleButton
                            className={cx('', {
                                'active': value === true
                            })}
                            sx={{
                                padding: 0,
                                paddingX: 2,
                                border: 0,
                                lineHeight: "32px",
                                textTransform: "unset",
                                borderRadius: "32px !important",
                                color: colors.red[400],
                                transition: "250ms",
                                "&.active, &.Mui-selected, &.Mui-selected:hover": {
                                    background: colors.green[400],
                                    color: theme.palette.mode === "dark" ? "rgba(0,0,0,.75)" : "#fff"
                                }
                            }}
                            value="true"
                        >
                            {t('yes')}
                        </ToggleButton>
                    </ToggleButtonGroup>
                );

            case "switch":
                return (
                    <ShSwitch
                        checked={value === true}
                        disabled={field.disabled}
                        onChange={(e) => updateFieldValue(field.name, e.target.checked, field)}
                    />
                );

            case "custom":
                // Caller supplies its own input via field.render for cases the built-in types can't express.
                return field.render
                    ? field.render({
                        value,
                        onChange: (newValue) => updateFieldValue(field.name, newValue, field),
                        formValues,
                        field,
                    })
                    : null;

            default:
                return null;
        }
    };

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            onKeyDown={handleKeyDown}
        >
            <Box
                sx={{
                    maxWidth: props.maxWidth ?? 500,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        textAlign: "center",
                        paddingY: 4,
                        paddingX: 5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Subheading>{t(props.title)}</Subheading>
                    {props.subtitle && props.subtitle.length > 0 &&
                        <Text center light sx={{whiteSpace: 'pre-line'}}>{t(props.subtitle)}</Text>
                    }
                </Box>

                {/* Optional message above the fields */}
                {props.message && (
                    <Box sx={{ display: "flex", justifyContent: "center", pb: 2 }}>
                        {props.message}
                    </Box>
                )}

                {/* Dynamic Fields */}
                {props.fields && props.fields.length > 0 && (
                    <Box
                        sx={{
                            pt: 3,
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            paddingLeft: 3,
                            paddingRight: 3,
                            background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            display: "flex",
                            flexDirection: "column",
                            gap: 2
                        }}
                    >
                        {props.fields.filter(field => !field.hidden?.(formValues)).map((field, index) => (
                            <Box key={field.name || index}
                    sx={{
                        width: field.width ?? 1,
                        display:"flex",
                        alignItems: field.alignTop ? "flex-start" : "center",
                        justifyContent:"space-between",
                        gap:7
                    }}>
                                {field.label && (
                                    <Box sx={{ mb: 0.5, width: props.labelWidth, display: "flex", alignItems: "center", height: field.alignTop ? 32 : undefined }}>
                                        <Text light>
                                            {t(field.label)}
                                            {field.required && <span style={{ color: colors.red[400] }}> *</span>}
                                        </Text>
                                        {field.explainerTitle && (<EMGeneralExplainerBox  title={field.explainerTitle} text={field.explainerSubTitle} />)}

                                    </Box>
                                )}
                                {renderField(field)}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Footer */}
                <Box
                    component="footer"
                    sx={{
                        display: "block",
                        width: 1,
                        paddingBottom: isStandAlone ? "32px" : "16px",
                        display: "flex",
                        alignItems: "center",
                        paddingRight: 3,
                        zIndex: 1058,
                        position: "relative",
                        paddingTop: 2,
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                        justifyContent: "space-between",
                        "@media screen and (max-width: 47.9375em)": {
                            paddingRight: 0,
                        }
                    }}
                >
                    <Shbutton
                        isCancel={true}
                        className={"always-visible"}
                        onClick={props.handleClose}
                        color="primary"
                        variant="text"
                        startIcon={<ArrowRightAltIcon/>}
                    >
                        {t("cancel")}
                    </Shbutton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {props.onSave && (
                        <SettingsSaveButton onClick={save} isLoading={isSaving} disabled={!hasChanges} />
                    )}
                    <ButtonGroup version={2}>
                        <Shbutton
                            className={"group-btn"}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            onClick={confirm}
                            variant="contained"
                        >
                            {t("confirm")}
                        </Shbutton>
                    </ButtonGroup>
                    </Box>
                </Box>
            </Box>
        </SHModal>
    );
};

export default MDynamicValues;
